const NATIVE_READY_EVENT = "tellingPapa:native-ready"
const PENDING_BRIDGE_STATE_STORAGE_KEY = "tellingPapa:pending-native-bridge-state"

const EMPTY_PENDING_STATE = {
  accessToken: null,
  clearAccessToken: null,
  shouldSyncPushToken: false,
}

let pendingBridgeState = { ...EMPTY_PENDING_STATE }
let nativeReadyListenerRegistered = false
let lastSetAccessToken = null
let lastClearedAccessToken = null
let lastPushSyncedAccessToken = null

const getCurrentWindow = () => {
  if (typeof window === "undefined") {
    return null
  }
  return window
}

const normalizeAccessToken = (accessToken) => {
  if (typeof accessToken !== "string") {
    return null
  }

  const normalizedAccessToken = accessToken.trim()
  if (!normalizedAccessToken) {
    return null
  }

  return normalizedAccessToken
}

const getPendingStateStorage = () => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow || !currentWindow.sessionStorage) {
    return null
  }

  return currentWindow.sessionStorage
}

const hasPendingBridgeState = (state) => {
  return Boolean(state.accessToken || state.clearAccessToken)
}

const readPersistedBridgeState = () => {
  const storage = getPendingStateStorage()
  if (!storage) {
    return { ...EMPTY_PENDING_STATE }
  }

  try {
    const rawPendingState = storage.getItem(PENDING_BRIDGE_STATE_STORAGE_KEY)
    if (!rawPendingState) {
      return { ...EMPTY_PENDING_STATE }
    }

    const pendingState = JSON.parse(rawPendingState)
    return {
      accessToken: null,
      clearAccessToken: normalizeAccessToken(pendingState.clearAccessToken),
      shouldSyncPushToken: false,
    }
  } catch (error) {
    return { ...EMPTY_PENDING_STATE }
  }
}

const readPendingBridgeState = () => {
  if (hasPendingBridgeState(pendingBridgeState)) {
    return { ...pendingBridgeState }
  }

  return readPersistedBridgeState()
}

const writePendingBridgeState = (pendingState) => {
  pendingBridgeState = {
    accessToken: normalizeAccessToken(pendingState.accessToken),
    clearAccessToken: normalizeAccessToken(pendingState.clearAccessToken),
    shouldSyncPushToken: Boolean(pendingState.shouldSyncPushToken) && Boolean(normalizeAccessToken(pendingState.accessToken)),
  }

  const storage = getPendingStateStorage()
  if (!storage) {
    return
  }

  try {
    if (!pendingBridgeState.clearAccessToken) {
      storage.removeItem(PENDING_BRIDGE_STATE_STORAGE_KEY)
      return
    }

    storage.setItem(PENDING_BRIDGE_STATE_STORAGE_KEY, JSON.stringify({
      clearAccessToken: pendingBridgeState.clearAccessToken,
    }))
  } catch (error) {
  }
}

const getNativeBridge = () => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow || !currentWindow.TellingPapaAppBridge) {
    return null
  }

  return currentWindow.TellingPapaAppBridge
}

function flushPendingBridgeState() {
  const pendingState = readPendingBridgeState()
  if (!pendingState.accessToken && !pendingState.clearAccessToken) {
    return false
  }

  const nativeBridge = getNativeBridge()
  if (!nativeBridge) {
    return false
  }

  if (pendingState.clearAccessToken) {
    if (typeof nativeBridge.clearAccessToken !== "function") {
      return false
    }

    if (lastClearedAccessToken !== pendingState.clearAccessToken) {
      nativeBridge.clearAccessToken(pendingState.clearAccessToken)
      lastClearedAccessToken = pendingState.clearAccessToken
    }

    if (lastSetAccessToken === pendingState.clearAccessToken) {
      lastSetAccessToken = null
    }
    lastPushSyncedAccessToken = null
    writePendingBridgeState(EMPTY_PENDING_STATE)
    return true
  }

  if (typeof nativeBridge.setAccessToken !== "function") {
    return false
  }

  if (lastSetAccessToken !== pendingState.accessToken) {
    nativeBridge.setAccessToken(pendingState.accessToken)
    lastSetAccessToken = pendingState.accessToken
    lastClearedAccessToken = null
  }

  if (pendingState.shouldSyncPushToken) {
    if (typeof nativeBridge.syncPushToken !== "function") {
      return false
    }

    if (lastPushSyncedAccessToken !== pendingState.accessToken) {
      nativeBridge.syncPushToken()
      lastPushSyncedAccessToken = pendingState.accessToken
    }
  }

  writePendingBridgeState(EMPTY_PENDING_STATE)
  return true
}

const ensureNativeReadyListener = () => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow || nativeReadyListenerRegistered) {
    return
  }

  currentWindow.addEventListener(NATIVE_READY_EVENT, flushPendingBridgeState)
  nativeReadyListenerRegistered = true
}

const removeNativeReadyListener = () => {
  const currentWindow = getCurrentWindow()
  if (!currentWindow || !nativeReadyListenerRegistered) {
    return
  }

  currentWindow.removeEventListener(NATIVE_READY_EVENT, flushPendingBridgeState)
  nativeReadyListenerRegistered = false
}

export const syncAccessTokenWithNative = (accessToken) => {
  const normalizedAccessToken = normalizeAccessToken(accessToken)
  if (!normalizedAccessToken) {
    return false
  }

  const pendingState = readPendingBridgeState()
  if (
    pendingState.accessToken === normalizedAccessToken &&
    pendingState.shouldSyncPushToken &&
    !pendingState.clearAccessToken
  ) {
    ensureNativeReadyListener()
    flushPendingBridgeState()
    return true
  }

  if (
    lastSetAccessToken === normalizedAccessToken &&
    lastPushSyncedAccessToken === normalizedAccessToken &&
    !pendingState.clearAccessToken
  ) {
    return true
  }

  ensureNativeReadyListener()
  writePendingBridgeState({
    accessToken: normalizedAccessToken,
    clearAccessToken: null,
    shouldSyncPushToken: true,
  })
  flushPendingBridgeState()
  return true
}

export const consumeLaunchAccessTokenFromNative = () => {
  const nativeBridge = getNativeBridge()
  if (!nativeBridge || typeof nativeBridge.consumeLaunchAccessToken !== "function") {
    return null
  }

  try {
    return normalizeAccessToken(nativeBridge.consumeLaunchAccessToken())
  } catch (error) {
    return null
  }
}

export const bootstrapNativeBridgeSync = () => {
  const pendingState = readPendingBridgeState()
  if (!pendingState.accessToken && !pendingState.clearAccessToken) {
    return false
  }

  ensureNativeReadyListener()
  flushPendingBridgeState()
  return true
}

export const clearAccessTokenFromNative = (accessToken) => {
  const normalizedAccessToken = normalizeAccessToken(accessToken)
  if (!normalizedAccessToken) {
    return false
  }

  const pendingState = readPendingBridgeState()
  if (pendingState.clearAccessToken === normalizedAccessToken && !pendingState.accessToken) {
    ensureNativeReadyListener()
    flushPendingBridgeState()
    return true
  }

  if (lastClearedAccessToken === normalizedAccessToken && !pendingState.accessToken) {
    return true
  }

  ensureNativeReadyListener()
  writePendingBridgeState({
    accessToken: null,
    clearAccessToken: normalizedAccessToken,
    shouldSyncPushToken: false,
  })
  flushPendingBridgeState()
  return true
}

export const __resetTellingPapaAppBridgeForTests = () => {
  removeNativeReadyListener()
  writePendingBridgeState(EMPTY_PENDING_STATE)
  pendingBridgeState = { ...EMPTY_PENDING_STATE }
  lastSetAccessToken = null
  lastClearedAccessToken = null
  lastPushSyncedAccessToken = null
}
